/**
 * @module app.core
 */

import { autoinject, transient } from "aurelia-framework";
import { ApiModelIdentifier } from 'app/shared/models/essette-api';
import { StandardLoadOptions } from 'app/shared/lib/essette-site';
import { isNullOrUndefined } from 'app/shared/lib/utilities';
import { IApiActionResults, IApiBatchProgress, IApiSettingsResults, IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { INotesConfig } from "app/core/models/essette-interfaces";
import * as api from 'app/shared/lib/essette-api';
import * as Core from 'app/core/models/essette-api';

const defaultConfig: INotesConfig = {
    serviceRootUrl: ""  // root of "Core" queries. Should not use this one because it lacks proper context for some of its actions
}

export interface INoteSearchFilter {
    i18nKey: string,
    filterValue: string,
    selected: boolean
}

export interface INoteFlagSearchFilters {
    allowCategories?: boolean | null;
    allowSubCategories?: boolean | null;
}

/** Returns the number of defined filters. Useful for checking that X filters exist before running a search */
export function countNoteFilters(searchFilters: INoteSearchFilter[]): number {
    const totalFilterCount = searchFilters.length;
    const selectedFilterCount = searchFilters.filter(filter => filter.selected).length
    if(!totalFilterCount || selectedFilterCount === totalFilterCount) return 0; // do not filter if all filters are selected
    else return selectedFilterCount;
}

@transient()
@autoinject
export class NoteService {

    ////****************** Fields and Properties ************************************////
    private isConfigured: boolean = false;
    private config: INotesConfig;


    ////****************** Functions ************************************************////
    constructor(protected api: api.Api) {
        this.config = defaultConfig;
    }

    public configure(config: INotesConfig): void {
        this.config = Object.assign({}, defaultConfig, config); // Clones the incoming config object parameter, using defaultConfig as fallback for any missing properties, and assigns it to this.config
        this.isConfigured = true;
    }

    private checkConfiguration(): void {
        if(!this.isConfigured) {
            throw Error("Note service requires module-specific configuration! Use configure() method.");
        }
    }

    public countNoteFilters(searchFilters: INoteSearchFilter[]): number {
        return countNoteFilters(searchFilters);
    }

    ////****************** Settings *************************************************////
    public async loadSettings(includes: string[] = []): Promise<IApiSettingsResults> {
        this.checkConfiguration();
        const url = `${this.config.serviceRootUrl}/notes`;
        return this.api.get(
            this.api.settings(url).include(...includes)
        );
    }


    ////****************** Queries **************************************************////
    public async loadNotesByTarget(targetId: ApiModelIdentifier, options?: StandardLoadOptions, includes: string[] = [], searchFilters: INoteSearchFilter[] = [], fields: string[] = []): Promise<api.ApiQueryResponse<Core.Note>> {
        this.checkConfiguration();
        const serviceRootUrl = this.config.rollupConfig?.serviceRootUrl || this.config.serviceRootUrl;
        const url = `${serviceRootUrl}/${targetId}` + `${this.config.includeRollup ? '/rollup' : ''}/notes`;
        const query = new api.ApiQuery(url)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        const searchFilterCount = countNoteFilters(searchFilters);
        if(searchFilterCount) {
            query.filterOr(query => {
                for(const filter of searchFilters.filter(filter => filter.selected)) {
                    query.filterString('attachedTo.type.code', filter.filterValue, 'eq');
                }
            });
        }

        if(this.config.flagCodes) {
            query.filterExists('flags', q => q.filterStringArray('code', this.config.flagCodes || []));
        }

        return await this.api.get(query);
    }

    public async loadDefinitions(options?: StandardLoadOptions, includes: string[] = []): Promise<api.ApiQueryResponse<Core.NoteDefinition>> {
        this.checkConfiguration();
        const query = new api.ApiQuery(`${this.config.serviceRootUrl}/notes/definitions`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadCategories(options?: Partial<IQueryLoadOptions>, includes: string[] = [], excludeSystem: boolean = true, withSubCategories: boolean = false, withDefinitions: boolean = false, fields: string[] = []): Promise<api.ApiQueryResponse<Core.NoteCategory>> {
        this.checkConfiguration();
        const query = this.api
            .query(`${this.config.serviceRootUrl}/notes/categories`)
            .applyStandardLoadOptions(options)
            .field(...fields);

        if(includes.length > 0) {
            query.detailTop(100000);
        }

        if(excludeSystem) {
            query.filterBoolean('flag.isSystem', false);
        }

        if(this.config.flagCodes) {
            query.filterExists('flags', q => q.filterStringArray('code', this.config.flagCodes || []));
        }

        if(withSubCategories) {
            query.includeWithState(options, 'subCategories')
                .filterExists('subCategories');

            if(!includes.includes('subCategories')) {
                includes.push('subCategories');
            }
        }

        if(withDefinitions) {
            query.includeWithState(options, 'definitions')
                .filterExists('definitions');

            if(!includes.includes('definitions')) {
                includes.push('definitions');
            }
        }

        query.include(...includes);

        return await this.api.get(query);
    }

    public async loadCategoryById(categoryId: string, includes: string[] = [], fields: string[] = []): Promise<api.ApiQueryResponse<Core.NoteCategory>> {
        this.checkConfiguration();
        const query = this.api
            .query(`${this.config.serviceRootUrl}/notes/categories/${categoryId}`)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadSubCategoriesByCategory(categoryId?: string, options?: StandardLoadOptions, includes: string[] = []): Promise<api.ApiQueryResponse<Core.NoteSubCategory>> {
        this.checkConfiguration();
        const query = new api.ApiQuery(`${this.config.serviceRootUrl}/notes/categories/${categoryId}/subCategories`)
            .applyStandardLoadOptions(options);
        includes.forEach(i => { query.include(i); });

        return await this.api.get(query);
    }

    public async loadFlagsByCategory(categoryId?: string, options?: StandardLoadOptions, includes: string[] = []): Promise<api.ApiQueryResponse<Core.NoteFlag>> {
        this.checkConfiguration();
        const query = new api.ApiQuery(`${this.config.serviceRootUrl}/notes/categories/${categoryId}/flags`)
            .applyStandardLoadOptions(options);
        includes.forEach(i => { query.include(i); });

        return await this.api.get(query);
    }

    public async loadDefinitionsByCategory(categoryId?: string, options?: StandardLoadOptions, includes: string[] = []): Promise<api.ApiQueryResponse<Core.NoteDefinition>> {
        this.checkConfiguration();
        const query = new api.ApiQuery(`${this.config.serviceRootUrl}/notes/categories/${categoryId}/definitions`)
            .applyStandardLoadOptions(options);
        includes.forEach(i => { query.include(i); });

        return await this.api.get(query);
    }

    public async loadNotesByCategoryAndTarget(categoryId: string, targetId: ApiModelIdentifier, options?: StandardLoadOptions, includes: string[] = []): Promise<api.ApiQueryResponse<Core.Note>> {
        this.checkConfiguration();
        const query = new api.ApiQuery(`${this.config.serviceRootUrl}/${targetId}/notes`)
            .filterString("category.id", categoryId, "eq")
            .applyStandardLoadOptions(options);
        includes.forEach(i => { query.include(i); });

        if(this.config.flagCodes) {
            query.filterExists('flags', q => q.filterStringArray('code', this.config.flagCodes || []));
        }

        return await this.api.get(query);
    }

    public async loadCategoryByCode(code: string, options?: StandardLoadOptions, excludeSystem: boolean = true, includes: string[] = []): Promise<api.ApiQueryResponse<Core.NoteCategory>> {
        this.checkConfiguration();
        const query = new api.ApiQuery(`${this.config.serviceRootUrl}/notes/categories`)
            .filterString('code', code, 'eq')
            .applyStandardLoadOptions(options);
        includes.forEach(i => { query.include(i); });

        if(excludeSystem) {
            query.filterBoolean('flag.isSystem', false);
        }

        if(this.config.flagCodes) {
            query.filterExists('flags', q => q.filterStringArray('code', this.config.flagCodes || []));
        }

        return await this.api.get(query);
    }

    public async loadNoteHistory(noteId: string, options?: StandardLoadOptions, includes: string[] = [], fields: string[] = []): Promise<api.ApiQueryResponse<Core.NoteHistory>> {
        const query = this.api
            .query(`/notes/${noteId}/history`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadNoteFlags(options?: StandardLoadOptions, includes: string[] = [], searchFilters?: INoteFlagSearchFilters): Promise<api.ApiQueryResponse<Core.NoteFlag>> {
        this.checkConfiguration();
        const query = this.api
            .query(`${this.config.serviceRootUrl}/notes/flags`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        if(searchFilters?.allowCategories) {
            query.filterBoolean('flag.allowCategories', true);
        }

        if(searchFilters?.allowSubCategories) {
            query.filterBoolean('flag.allowSubCategories', true);
        }

        return await this.api.get(query);
    }

    ////****************** Loads ****************************************************////


    ////****************** Actions **************************************************////
    public async saveNote(note: Core.Note): Promise<api.ApiPostResponse> {
        this.checkConfiguration();
        const actionName = !note.id ? 'insert' : 'update';
        const url = !note.id ? `${this.config.serviceRootUrl}/notes` : `/notes/${note.id}`;

        const action = new api.ApiAction(actionName, url, note);
        return await this.api.post(action);
    }

    public async insertNoteBatch(notes: Core.Note[], progress?: IApiBatchProgress): Promise<IApiActionResults[]> {
        this.checkConfiguration();
        const batch = this.api.batch();
        for(const n of notes) {
            batch.post(this.api.action("insert", `${this.config.serviceRootUrl}/notes`, n))
        }
        return await this.api.execute(batch, { progress });
    }

    public async activateNote(noteId: string, isActive: boolean, reason: string): Promise<IApiActionResults> {
        this.checkConfiguration();
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const url = `${this.config.serviceRootUrl}${this.config.includeRollup ? '/rollup' : ''}/notes/${noteId}`;
        const action = this.api
            .action(actionCode, url, { isActive: isActive })

        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action.withReason(reason));
        }
    }

    public async saveNoteCategory(noteCategory: Core.NoteCategory): Promise<api.ApiPostResponse> {
        this.checkConfiguration();
        const actionName = !noteCategory.id ? 'insert' : 'update';
        const url = !noteCategory.id ? `${this.config.serviceRootUrl}/notes/categories` : `${this.config.serviceRootUrl}/notes/categories/${noteCategory.id}`;

        const action = new api.ApiAction(actionName, url, noteCategory);
        return await this.api.post(action);
    }

    public async activateNoteCategory(noteCategoryId: string, isActive: boolean): Promise<IApiActionResults> {
        this.checkConfiguration();
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const url = `${this.config.serviceRootUrl}/notes/categories/${noteCategoryId}`;
        const action = this.api.action(actionCode, url, { isActive });
        return isActive ? await this.api.post(action) : await this.api.delete(action);
    }

    public async saveNoteSubCategory(noteSubCategory: Core.NoteSubCategory): Promise<api.ApiPostResponse> {
        this.checkConfiguration();
        const actionName = !noteSubCategory.id ? 'insert' : 'update';
        const url = !noteSubCategory.id ? `${this.config.serviceRootUrl}/notes/subCategories` : `${this.config.serviceRootUrl}/notes/subCategories/${noteSubCategory.id}`;
        const action = new api.ApiAction(actionName, url, noteSubCategory);
        return await this.api.post(action);
    }

    public async activateNoteSubCategory(noteSubCategoryId: string, isActive: boolean): Promise<IApiActionResults> {
        this.checkConfiguration();
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const url = `${this.config.serviceRootUrl}/notes/subCategories/${noteSubCategoryId}`;
        const action = this.api.action(actionCode, url, { isActive });
        return isActive ? await this.api.post(action) : await this.api.delete(action);
    }

    public async saveStandardNote(standardNote: Core.NoteDefinition): Promise<api.ApiPostResponse> {
        this.checkConfiguration();
        const actionName = !standardNote.id ? 'insert' : 'update';
        const url = !standardNote.id ? `${this.config.serviceRootUrl}/notes/definitions` : `${this.config.serviceRootUrl}/notes/definitions/${standardNote.id}`;
        const action = new api.ApiAction(actionName, url, standardNote);
        return await this.api.post(action);
    }

    public async activateStandardNote(standardNoteId: string, isActive: boolean): Promise<IApiActionResults> {
        this.checkConfiguration();
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const url = `${this.config.serviceRootUrl}/notes/definitions/${standardNoteId}`;
        const action = this.api.action(actionCode, url, { isActive });
        return isActive ? await this.api.post(action) : await this.api.delete(action);
    }
}
